import {Injectable, Inject, EventEmitter, signal} from '@angular/core';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {emtrConstant} from '../../constants/emtrConstants';
import {ConfigUrlService, SharedService, SharedConstant} from '@ems/shared';

@Injectable({
  providedIn: 'root'
})
export class RuleService {

  getConstant = emtrConstant;
  private subject = new ReplaySubject<any>();
  orgId: string;
  configURL = this.config.setApplicationUrl();
  showSchError = new EventEmitter<boolean>();
  showRequestIdError = new EventEmitter<boolean>();
  schErrorMsg = new EventEmitter<string[]>();
  toastMsg  = new EventEmitter<string>();
  toastType  = new EventEmitter<string>();
  duplicateRuleName = new BehaviorSubject(null);
  showSelectionMsg  = new EventEmitter<string>();
  scrollHeightValue = 0 ;
  checkIfChangesMadeInStep1 = false;
  checkIfChangesMadeInStep2 = false;
  filterClickedSubHeader = new EventEmitter<boolean>();
  invalidTradeSymbolField = new EventEmitter<boolean>();
  invalidCusipSymbolField = new EventEmitter<boolean>();
  clearClicked = new EventEmitter<boolean>();
  hasValueTradeSymbolField = new EventEmitter<boolean>();
  hasValueCusipField = new EventEmitter<boolean>();
  hasGroupPlanValue = new EventEmitter<boolean>();
  specialCharValidation = new EventEmitter<boolean>();
  hasValueViewField = new EventEmitter<boolean>();
  hasInstrumentGroupValue = new EventEmitter<boolean>();
  isSubHeaderFlag: boolean;
  previousStep = false;
  businessUnit = '';
  deletedRuleInfo = false;
  presetRuleRadioSelected = new EventEmitter<boolean>();
  oldRBSRuleNameForCopyFlow = '';
  originalRBSRuleNameForCopyFlow = '';
  rbsTwoStepGeneratedRuleName = '';
  globalOrSemiGlobalRuleName = '';
  globalRuleInfo: { type: string } | null = null;
  invalidTickerSymbolField = new EventEmitter<boolean>();
  invalidCusipSymbolFieldIG = new EventEmitter<boolean>();
  specialCharValidationForCusip = new EventEmitter<boolean>();
  invalidNameField = new EventEmitter<boolean>();
  invalidSedolField = new EventEmitter<boolean>();
  invaliIsinField = new EventEmitter<boolean>();
  specialCharValidationForSedol = new EventEmitter<boolean>();
  specialCharValidationForIsin = new EventEmitter<boolean>();
  k4ClientName = '';

  // ── Cache Signals ──
  private _assetGrpCache = signal<any[]>([]);
  private _presetRulesCache = signal<any[]>([]);

  // ── Public read-only access ──
  readonly assetGrpCache = this._assetGrpCache.asReadonly();
  readonly presetRulesCache = this._presetRulesCache.asReadonly();


  constructor(private httpService: HttpClient, @Inject('env') public env, public config: ConfigUrlService, public sharedService: SharedService) {
    this.sharedService.selectedClientInfo.subscribe(clientInfo => {
      if (clientInfo) {
        this.orgId = clientInfo && clientInfo.orgId;
        if (this.businessUnit && this.businessUnit !== clientInfo.businessUnit) {
          this.clearCaches();
        }
        this.businessUnit = clientInfo.businessUnit;
        this.k4ClientName = clientInfo.k4ClientName;
      }
    });
  }

  checkRuleNameExists(reqPayload): Observable<any> {
    this.sharedService.decodeUrl = false;
    const schwabClientHeader = this.orgId ? 
      this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, []) : [];
    
    const ruleNameNew = encodeURIComponent(reqPayload.ruleName);
    const baseUrl = this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');

    if (reqPayload.OBPCheck) {
      return this.httpService.get(baseUrl + '&ruleSubCategory=' + SharedConstant.openBrokerage.subType);
    }

    const planIdParam = reqPayload.planId ? `&planId=${reqPayload.planId}` : '';
    return this.httpService.get(baseUrl + `&name=${ruleNameNew}` + planIdParam);
  }

  saveRule(ruleObj, ruleName?): Observable<any> {
    let schwabClientHeader = [];
    if (ruleName) {
    schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.ruleName, encodeURIComponent(ruleName), schwabClientHeader);
    }
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    const url = this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    if (ruleObj.id) {
      return this.httpService.put(url, ruleObj);
    } else {
      return this.httpService.post(url, ruleObj);
    }
  }

  getRules(pageObj, formFilterObj): Observable<any> {
    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    const ruleFilterInfo = formFilterObj;
    this.sharedService.stateValues.filterObj = ruleFilterInfo;
    this.sharedService.stateValues.pageObj.pageNo = pageObj.pageNo;
    this.sharedService.stateValues.pageObj.pageSize = pageObj.pageSize;
    this.sharedService.setStateVal(JSON.stringify(this.sharedService.stateValues));
    this.sharedService.tableDropdownVal.next(pageObj.pageSize);
    const sortField = (pageObj.sortField) ? pageObj.sortField.map(data => '&sortField=' + data).join('') : '';
    const endPoint = this.configURL.bffRulesSummaryEndPointV1 + '&pageNumber=' + (pageObj.pageNo + 1) + '&pageSize=' + pageObj.pageSize + sortField + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    return this.httpService.post(endPoint, ruleFilterInfo);
  }

  sendMessage(message: any) {
    this.subject.next({text: message});
  }

  getMessage(): Observable<any> {
    return this.subject.asObservable();
  }

  clearMessage() {
    this.subject.next({text: ''});
  }

  getRuleData(ruleId: number, type) {
    let schwabClientHeader = [];
    let url = '';
    if ((type && type.type) && (type.type === 'GLOBAL' || type.type === 'SEMI_GLOBAL')) {
      schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.ruleId, ruleId, schwabClientHeader);
      url = this.configURL.bffRulesEndPointV1 + '&globalRuleType=' + type.type + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    } else {
      schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.ruleId, ruleId, schwabClientHeader);
      this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
      url = this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader  + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    }
    return this.httpService.get(url);
  }

  saveEditRuleRecurring(ruleObj): Observable<any> {
    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    return this.httpService.post(this.configURL.rulesEndPoint + '/recurring' + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU'), ruleObj);
  }

  saveEditRuleCopy(ruleObj): Observable<any> {
    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    return this.httpService.post(this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU'), ruleObj);
  }

  copyAndUnassignRule(ruleObj): Observable<any> {
    // Extract k4 value from k4ClientName (format: "k4 - name")
    const masterAccount = this.k4ClientName ? this.k4ClientName.split('-')[0].trim() : '';

    const copyAndUnassignPayload = {
      orgId: this.orgId,
      ruleType: ruleObj.subType,
      rulesCategory: ruleObj.rulesCategory,
      startDt: ruleObj.startDt,
      id: ruleObj.id,
      action: 'COPY_UNASSIGN',
    };

    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.masterAccount, masterAccount, schwabClientHeader);
    return this.httpService.post(this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU'), copyAndUnassignPayload);
  }

  unassignRule(ruleObj): Observable<any> {
    // Extract k4 value from k4ClientName (format: "k4 - name")
    const masterAccount = this.k4ClientName ? this.k4ClientName.split('-')[0].trim() : '';

    const unassignPayload = {
      id: ruleObj.id,
      unAssignSubAcctNumbers: [
        {
          empId: '',
          subAcctNum: masterAccount
        }
      ]
    };

    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.ruleId, ruleObj.id, schwabClientHeader);
    schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.masterAccount, masterAccount, schwabClientHeader);
    return this.httpService.put(this.configURL.bffRulesAssignmentEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&action=UNASSIGN' + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU'), unassignPayload);
  }

  getSecurities(isRestrictShortSellsChecked: boolean) {
    return this.httpService.get(this.configURL.bffClassificationEndPointV1 + '&shortsell=' + isRestrictShortSellsChecked);
  }

  deleteRule(ruleId: number, ruleName?: string ) {
    let schwabClientHeader = [];
    schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.ruleId, ruleId, schwabClientHeader);
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    if (ruleName) {
    schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.ruleName, encodeURIComponent(ruleName), schwabClientHeader);
    }
    return this.httpService.delete(this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU'));
  }

  getAssettypes() {
    return this.httpService.get(this.configURL.bffSearchAssetTypeEndPointV1);
  }

  getAutoSecurities(query, type): Observable<any> {
    return this.httpService.get(this.configURL.bffSecurityEndPointV1 + '&searchValue=' + (query ? query.trim() : '') + '&searchType=' + type);
  }

  getSearchSecurities(searchObj): Observable<any> {
    if (searchObj.securityType.indexOf(this.getConstant.rulesConstant.autoComplete.exactMatchSymbol) !== -1 || searchObj.securityType.indexOf(this.getConstant.rulesConstant.autoComplete.exactMatchCusip) !== -1) {
      return this.httpService.get(this.configURL.bffSecurityEndPointV1 + '&searchValue=' + (searchObj.securitySearch ? encodeURIComponent(searchObj.securitySearch.trim()) : '') + '&searchType=' + searchObj.securityType);
    } else {
      return this.httpService.get(this.configURL.bffSecurityEndPointV1 + '&searchValue=' + (searchObj.securitySearch ? encodeURIComponent(searchObj.securitySearch.trim()) : '') + '&securityType=' + searchObj.securityType + '&page=' + searchObj.page + '&size=' + searchObj.size);
    }

  }

  assignEmployees(empObj): Observable<any> {
    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    const url = this.configURL.bffRulesAssignmentEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    return this.httpService.put(url, empObj, {observe: 'response'});
  }

  putSaveCall(saveObj): Observable<any> {
    let schwabClientHeader = [];
    this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    const url = this.configURL.bffRulesEndPointV1 + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    return this.httpService.put(url, saveObj, {observe: 'response'});
  }

  restoreRule(ruleId, type): Observable<any> {
    let schwabClientHeader = [];
    if (type === null) {
      this.orgId && (schwabClientHeader = this.sharedService.setSchwabClientHeaders(this.sharedService.applicationInfo.data.queryParamMap.orgId, this.orgId, schwabClientHeader));
    }
    const url = this.configURL.bffRulesEndPointV1 + '&ruleId=' + ruleId + '&active=' + true + '&Schwab-Client-Ids=' + schwabClientHeader + '&ruleViewType=' + (this.businessUnit ? 'CLIENT' : 'BU');
    return this.httpService.put(url, '', {observe: 'response'});
  }

  validateNameAndPlanId(message, form) {
    const planIdTxt = 'planId';
    let validationfield;
    if (message === this.getConstant.rule10b5Msg.duplicateRuleNameAndPlanId) {
      form.controls[planIdTxt].setErrors({incorrect: true});
      validationfield = 'both';
    } else if (message === this.getConstant.rule10b5Msg.duplicatePlanId) {
      form.controls[planIdTxt].setErrors({incorrect: true});
      validationfield = 'planId';
    } else if (message === this.getConstant.rule10b5Msg.duplicateRuleName) {
      const ruleNameExistsMsg = this.getConstant.rulesConstant.ruleNameMsg.the + '<strong> "' + form.get('ruleName').value + '"</strong>' + this.getConstant.rulesConstant.ruleNameMsg.ruleNameExistWarningMsg;
      this.duplicateRuleName.next(true);
      /*To show the rulename exists warning msg*/
      this.toastType.emit('warn');
      this.toastMsg.emit(ruleNameExistsMsg);
      validationfield = 'ruleName';
    }
    return validationfield;
  }

  getAssetGroups(): Observable<any> {
    return this.httpService.get(this.configURL.bffAssetGroupEndPointV1 + '&businessUnit=' + (this.businessUnit ? this.businessUnit : 'RBS'));
  }

  getPresetRules(): Observable<any> {
    return this.httpService.get(this.configURL.bffPresetRulesEndPointV1);
  }

    
  // ── Cache methods ──

  setAssetGrpCacheData(assetGrpData: any[]) {
    /* Storing the asset group api response in cache */
    if (assetGrpData && assetGrpData.length > 0) {
      const mapped = assetGrpData.map(val => ({
        bu: val.bu,
        code: val.code ? val.code : '',
        id: val.id,
        name: val.name,
        label: val.label ? val.label : '',
        attributes: val.attributes ? val.attributes : [],
        secCatgs1: val.secCatgs1,
        secCatgs2: val.secCatgs2,
        secCatgs3: val.secCatgs3,
        order: val.order,
        isDeprecated: val.isDeprecated
      }));
      this._assetGrpCache.set(mapped);
    } else {
      this._assetGrpCache.set([]);
    }
  }

  setPresetRulesCacheData(data: any[]): void {
    this._presetRulesCache.set(data);
  }

  clearCaches(): void {
    this._assetGrpCache.set([]);
    this._presetRulesCache.set([]);
  }


   getTotalAttributeCount(code: string): number {
    const cachedGroup = this.assetGrpCache().find(grp => grp.code === code);
    return cachedGroup?.attributes?.length ?? 0;
  }

  maintainAssetGroupOrder(formData) {
    const displaySelectedAssetGrp = [];
    const assetGrp = formData.assetGroups;
    assetGrp.forEach(val => {
      /* Filter the selected asset grp value from the cache one after the other by comparing the code of the asset group */
      const localArr = this.assetGrpCache().filter(x => x.code === val.code);
      /* If no matching asset group is found in the cache, skip ordering for this entry to avoid undefined access */
      if (!localArr[0]) {
        displaySelectedAssetGrp.push(val);
        return;
      }
      /* After filtering the first value in the selected asset grp, attach the order value to the selected group which
      would be present in the localArr - localArr will contain only 1 object bec we will be filtering one asset group at
      a time */
      val.order = localArr[0].order;
      /* Now iterate the attributes of the selected asset group to attach the order value of the each attribute */
      val.attributes.forEach(attributeVal => {
        /* We will be filtering 1 attribute at a time based on the code to retrieve the order of the each attribute from
        the localArr and attach it to the selected attribute */
        const localAtrributeArr = localArr[0].attributes.filter(x => x.code === attributeVal.code);
        if (localAtrributeArr[0]) {
          attributeVal.order = localAtrributeArr[0].order;
        }
      });
      displaySelectedAssetGrp.push(val);
    });

    /* Once our displaySelectedAssetGrp is ready with selected asset group value and its corresponding order value,
    sort both at the asset group level and attribute level so that both asset group and its attributes are displayed
    in the required order */
    if (displaySelectedAssetGrp && displaySelectedAssetGrp.length > 0) {
      displaySelectedAssetGrp.sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0));
      displaySelectedAssetGrp.forEach(latAttrbts => {
        latAttrbts.attributes.sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0));
      });
    }

    return displaySelectedAssetGrp;
  }

  uploadSecurities(uploadObj): Observable<any> {
    const url = this.configURL.bffSecurityEndPointV1 + '&attributeName=SECURITIESLIST&page=0&size=300';
    return this.httpService.post(url, uploadObj);
  }

  /* To update the secCatgs value in the preset rules from the asset group secCatgs. In preset rules we only get the missing securities other than asset groups
  * hence to populate all the required value we need to get all the securities from preset rules and asset groups */
  updateSecCatgsInPresentRule(presetRules, assetGroups) {
    const updatePresetList = presetRules;
    /* Fetch each rule in the preset rules list */
    updatePresetList.forEach(rule => {
      const secCatgs1 = [];
      const secCatgs2 = [];
      const secCatgs3 = [];
      /* Iterate through the asset group list of the respective rule and check the assent group in rule against the asset groups list and fetch the corresponding all 3 secCatgs */
      /* Iterate through secCatgs1/secCatgs2/secCatgs3 array of assetGroups list, if the value is present then loop through the value and push each value into the const variable */
      rule.assetGroups.filter(asstGrp1 => assetGroups.some(grp => asstGrp1.name === grp.name && grp.secCatgs1 ? grp.secCatgs1.filter(security => secCatgs1.push(security)) : ''));
      rule.assetGroups.filter(asstGrp1 => assetGroups.some(grp => asstGrp1.name === grp.name && grp.secCatgs2 ? grp.secCatgs2.filter(security => secCatgs2.push(security)) : ''));
      rule.assetGroups.filter(asstGrp1 => assetGroups.some(grp => asstGrp1.name === grp.name && grp.secCatgs3 ? grp.secCatgs3.filter(security => secCatgs3.push(security)) : ''));

      /* Once the secCatgs is formed, then loop through secCatgs1/secCatgs2/secCatgs3 of rule and depending on the condition either [push or assign the values fetched from Asset group list */
      secCatgs1 && secCatgs1.length > 0 ? rule.secCatgs1 ? secCatgs1.filter(sec => rule.secCatgs1.indexOf(sec) === -1 ? rule.secCatgs1.push(sec) : '') : rule.secCatgs1 = secCatgs1 : '';
      secCatgs2 && secCatgs2.length > 0 ? rule.secCatgs2 ? secCatgs2.filter(sec => rule.secCatgs2.indexOf(sec) === -1 ? rule.secCatgs2.push(sec) : '') : rule.secCatgs2 = secCatgs2 : '';
      secCatgs3 && secCatgs3.length > 0 ? rule.secCatgs3 ? secCatgs3.filter(sec => rule.secCatgs3.indexOf(sec) === -1 ? rule.secCatgs3.push(sec) : '') : rule.secCatgs3 = secCatgs3 : '';
    });
    return updatePresetList;
  }
  
  mapAssetGroupNames(assetGroups) {
    // Map name and attributes from cached data while preserving other properties
    assetGroups = assetGroups.map(assetGrp => {
      const cachedAssetGrp = this.assetGrpCache().find(cached => cached.code === assetGrp.code);

      if (cachedAssetGrp) {
        // Map attributes by comparing codes and filter only matching attributes from both endpoints
        const updatedAttributes = assetGrp.attributes?.filter(attr => {
          const cachedAttr = cachedAssetGrp.attributes?.find(cached => cached.code === attr.code);
          return cachedAttr; // Only keep attributes that exist in cached data
        }).map(attr => {
          const cachedAttr = cachedAssetGrp.attributes?.find(cached => cached.code === attr.code);
          return { ...attr, name: cachedAttr.name };
        }) || [];

        return { ...assetGrp, name: cachedAssetGrp.name, attributes: updatedAttributes };
      }
      return assetGrp;
    });
    return assetGroups;
  }

  getAssetGroupCheckboxClass(item: any, businessUnit: string, totalAttributes?: number): string {
    const checkedClass = 'sch-check-circle';
    const partialClass = 'sch-minus-square-large font-21';  
    // Digital Asset Group: show partial when fewer than total attributes are selected.
    if (item.code === this.getConstant.ruleSteps.step2.digitalAssetGroupCode) {
      const total = totalAttributes ?? item.attributes.length;
      return (item.attributes.length > 0 && item.attributes.length < total) ? partialClass : checkedClass;
    }
    // DBS: any attribute selected counts as fully restricted (checkedClass), including partial
    // selections. The review template reuses this class to decide the non-override delete icon,
    // so a DBS partial selection renders as fully restricted there too.
    if (businessUnit === SharedConstant.dbsBUText && item.attributes.length > 0) {
      return checkedClass;
    }
    // Existing behavior for other asset groups.
    return item.attributes.length > 0 ? partialClass : checkedClass;
  }

  /* Append incoming security nodes into target only when a node with the same code + level
     is not already present, so the review security details modal does not render duplicate rows. */
  appendUniqueSecurityNodes(target: any[], incoming: any[]) {
    (incoming || []).forEach((item: any) => {
      const code = item?.data?.code ?? item?.code ?? item?.secTypCd;
      const level = item?.data?.level ?? item?.level ?? '';
      if (!code) {
        return;
      }
      const exists = target.some((existing: any) => {
        const existingCode = existing?.data?.code ?? existing?.code ?? existing?.secTypCd;
        const existingLevel = existing?.data?.level ?? existing?.level ?? '';
        return existingCode === code && existingLevel === level;
      });
      if (!exists) {
        target.push(item);
      }
    });
  }

  /* Format the backend security levels JSON (with nested subLevels) into the tree-node
     shape ({ data, children, expanded }) used by the review security details modal. */
  formatSecurityNodes(data: any[]): any[] {
    return (data || []).map((dataEle: any) => {
      const subLevels = dataEle.subLevels;
      const obj: any = {
        data: dataEle
      };
      if (subLevels) {
        obj.children = this.formatSecurityNodes(subLevels);
        obj.expanded = true;
      }
      return obj;
    });
  }
}
